/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@components/DataTable";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-light-svg-icons";
import useListReleaseFlags from "./api/useListReleaseFlags";
import useDeleteReleaseFlag from "./api/useDeleteReleaseFlag";
import { ReleaseFlag, STATUS_LABELS } from "../types";
import useStyles from "../styles/useStyles";

const statusClassFor = (
  status: ReleaseFlag["status"],
  styles: ReturnType<typeof useStyles>
) => {
  switch (status) {
    case "building":
      return [styles.statusPill, styles.statusBuilding];
    case "beta_test":
      return [styles.statusPill, styles.statusBeta];
    case "released":
      return [styles.statusPill, styles.statusReleased];
  }
};

const Show: React.FC = () => {
  const navigate = useNavigate();
  const styles = useStyles();
  const { releaseFlags, isLoading, refetch } = useListReleaseFlags();
  const { deleteMany, isLoading: isDeleting } = useDeleteReleaseFlag();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFail, setShowFail] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const selectedFlags = releaseFlags.filter((f) => selectedIds.includes(f.id));

  const columns: ColumnDef<ReleaseFlag>[] = [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <span
          css={styles.nameCell}
          onClick={() =>
            navigate(`/admin/release_flags/${row.original.id}/edit`)
          }
        >
          {row.original.name}
        </span>
      ),
    },
    {
      header: "Key",
      accessorKey: "key",
      cell: ({ row }) => <span css={styles.monospace}>{row.original.key}</span>,
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <span css={statusClassFor(row.original.status, styles)}>
          {STATUS_LABELS[row.original.status]}
        </span>
      ),
    },
    { header: "Beta Date", accessorKey: "betaReleaseDate" },
    { header: "GA Date", accessorKey: "generalAvailabilityDate" },
  ];

  const handleConfirmDelete = async () => {
    const ids = selectedFlags.map((f) => f.id);
    try {
      await deleteMany(ids);
      const count = ids.length;
      setResponseMessage(
        count === 1 ? "Release flag deleted" : `${count} release flags deleted`
      );
      setShowSuccess(true);
      setSelectedIds([]);
      setShowConfirm(false);
      refetch();
    } catch (err: any) {
      const detail =
        err?.data?.errors?.[0]?.detail || "Could not delete release flag(s).";
      setResponseMessage(detail);
      setShowFail(true);
      setShowConfirm(false);
    }
  };

  if (isLoading) return <ScopeStackSpinner />;

  const renderToolbar = () =>
    selectedIds.length > 0 ? (
      <div css={styles.selectedToolbar}>
        <span css={styles.selectedMessage}>{selectedIds.length} Selected</span>
        <div css={styles.selectedIcons}>
          <button
            type="button"
            aria-label="Delete selected"
            onClick={() => setShowConfirm(true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>
    ) : null;

  const renderConfirm = () => (
    <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          Delete release flag{selectedFlags.length === 1 ? "" : "s"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Delete {selectedFlags.length} release flag
          {selectedFlags.length === 1 ? "" : "s"}? This removes the ScopeStack
          record only — the flag will continue to exist in LaunchDarkly.
        </p>
        {selectedFlags.length <= 5 && (
          <ul>
            {selectedFlags.map((f) => (
              <li key={f.id}>{f.name}</li>
            ))}
          </ul>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button className="btnSeawhite" onClick={() => setShowConfirm(false)}>
          Cancel
        </Button>
        <Button
          className="squareGreenButton"
          onClick={handleConfirmDelete}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderHeader = () => (
    <Card.Header
      style={{
        backgroundColor: "#fff",
        color: "#1C2655",
        padding: "0px 16px",
      }}
    >
      <Row style={{ padding: "20px 0 0 20px", alignItems: "center" }}>
        <Col className="text24" sm={7}>
          Release Flags
        </Col>
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              float: "right",
            }}
          >
            <Button
              className="squareGreenButton"
              onClick={() => navigate("/admin/release_flags/new")}
              style={{ marginRight: "16px", marginLeft: "10px" }}
            >
              New Release Flag
            </Button>
          </div>
        </Col>
      </Row>
    </Card.Header>
  );

  if (releaseFlags.length === 0) {
    return (
      <div className="releaseFlagsPage legacyPageBleed">
        <Card>
          {renderHeader()}
          <Card.Body css={styles.emptyState}>
            <h4>No release flags yet.</h4>
            <p>Create one to track a feature you're preparing to release.</p>
            <Button
              className="squareGreenButton"
              onClick={() => navigate("/admin/release_flags/new")}
            >
              New Release Flag
            </Button>
          </Card.Body>
        </Card>
        {handleAlert(
          showSuccess,
          responseMessage,
          setShowSuccess,
          "success",
          ToastAlert
        )}
        {handleAlert(
          showFail,
          responseMessage,
          setShowFail,
          "warning",
          ToastAlert
        )}
      </div>
    );
  }

  return (
    <div className="releaseFlagsPage legacyPageBleed">
      <Card>
        {renderHeader()}
        <Card.Body>
          {renderToolbar()}
          <DataTable
            data={releaseFlags}
            columns={columns}
            striped
            hover
            selectable
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        </Card.Body>
      </Card>
      {renderConfirm()}
      {handleAlert(
        showSuccess,
        responseMessage,
        setShowSuccess,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFail,
        responseMessage,
        setShowFail,
        "warning",
        ToastAlert
      )}
    </div>
  );
};

export default Show;
